import React from 'react'
import 'bootstrap/dist/css/bootstrap.min.css'
import Parse from 'parse'
import {
  getStrokeString,
  formatSeconds,
  getGoalName,
  getPreferredWorkoutLengthDisplay,
  convertPTI
} from './../Utils'
import PropTypes from 'prop-types'
import DatePicker from 'react-datepicker'
import OtpInput from 'react-otp-input'
import Modal from 'react-modal'
import 'react-datepicker/dist/react-datepicker.css'
import { format, parseISO, formatISO } from 'date-fns'
import Config from './../config'
import { getSourceMeta } from './../Tools/swimCoachConversationSource'
import GarminHistoryBackfillComponent from './GarminHistoryBackfillComponent'
import BoundedListComponent from '../Shared/BoundedListComponent'
import DetailSectionComponent, { DetailMore } from '../Shared/DetailSectionComponent'
import DetailFieldComponent, { DetailGrid } from '../Shared/DetailFieldComponent'
import DetailJumpNavComponent from '../Shared/DetailJumpNavComponent'
import DetailStatComponent from '../Shared/DetailStatComponent'
import RawFieldsComponent from '../Shared/RawFieldsComponent'
import {
  EMPTY,
  describeRawValue,
  formatBool,
  formatDate,
  formatDateTime,
  formatNumber,
  formatUtcOffset,
  isBlank,
  joinSummary,
  rawFieldsSummary,
  toDate
} from '../Shared/detailFormat'
import CompletedActivitiesTableComponent from './CompletedActivitiesTableComponent'
import FieldHistoryComponent from './FieldHistoryComponent'
import MemberActivityDataComponent from './MemberActivityDataComponent'
import YearlyTotalsTableComponent from './YearlyTotalsTableComponent'
import {
  SCHEDULE_DAYS_SHOWN_FIELDS,
  SETTINGS_SHOWN_FIELDS,
  SKILL_LEVEL_LABELS,
  TRAINING_FOCUS_LABELS,
  TRAINING_LEGACY_FIELDS,
  TRAINING_SHOWN_FIELDS,
  USER_INFO_PROFILE_LEGACY_FIELDS,
  USER_INFO_SHOWN_FIELDS,
  USER_INFO_STATS_LEGACY_FIELDS,
  USER_LEGACY_FIELDS,
  USER_POOL_LEGACY_FIELDS,
  USER_SHOWN_FIELDS,
  USER_SUBSCRIPTION_LEGACY_FIELDS,
  SETTINGS_LEGACY_FIELDS,
  activePlanRows,
  activeSwimDays,
  describeAge,
  describeEnum,
  describePlanKey,
  describeSettingsFlag,
  describeWeight,
  mergeFieldHistory,
  yearlyTotalsRows
} from './memberFields'

// Auth provider values as the backend stores them: linked providers in _User.authData, and
// the most recently used one in UserInfo_v3.loginMethod (which also uses 'password').
const AUTH_PROVIDER_LABELS = {
  apple: 'Apple',
  google: 'Google',
  facebook: 'Facebook',
  twitter: 'Twitter',
  password: 'Email/Password'
}

// In page order. The first five are open on load; the rest are collapsed and
// the jump opens them.
const MEMBER_SECTIONS = [
  { id: 'member-account', label: 'Account' },
  { id: 'member-subscription', label: 'Subscription' },
  { id: 'member-training', label: 'Training' },
  { id: 'member-stats', label: 'Stats' },
  { id: 'member-activities', label: 'Activities' },
  { id: 'member-profile', label: 'Profile' },
  { id: 'member-preferences', label: 'Preferences' },
  { id: 'member-integrations', label: 'Integrations' },
  { id: 'member-coach', label: 'Coach' },
  { id: 'member-achievements', label: 'Achievements' },
  { id: 'member-experiments', label: 'Experiments' },
  { id: 'member-history', label: 'History' },
  { id: 'member-raw', label: 'All fields' }
]

// A list's length for a collapsed header; nothing while it loads.
const countText = (list) => (Array.isArray(list) ? String(list.length) : null)

const joinList = (values) =>
  Array.isArray(values) && values.length > 0 ? values.join(', ') : null

// An OAuth 2 link has no OAuth 1 token, so either one means linked.
const hasGarminLink = (member) =>
  !!(member?.get('garminAccessToken') ||
    member?.get('garminOAuth2AccessToken'))

const options = [
  { value: '0', label: '1' },
  { value: '1', label: '2' },
  { value: '2', label: '3' },
  { value: '3', label: '4' },
  { value: '4', label: '5' }
]

const customStyles = {
  content: {
    top: '55%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    height: '85%'
  }
}

export default class MemberDetail extends React.Component {
  constructor (props) {
    super(props)
    if (!Parse.User.current()) window.href = '/'
    if (this.props.match.params.id) {
      this.state = {
        member: null,
        userInfo: null,
        settings: null,
        achievements: null,
        training: null,
        dynamicIntervals: null,
        allAchievements: null,
        otp: null,
        modalIsOpen: false,
        form: {
          skillLevel: null,
          garminAccessToken: null,
          coachCallDate: '',
          onboardingCallDate: ''
        },
        subscriptionExtensions: [],
        coachInfo: null,
        isCoach: false,
        assignedUsers: [],
        userRoles: [],
        isCreatingWelcomeChat: false,
        isCreatingEmptyChat: false,
        isResettingStreak: false,
        isRecalculatingStreak: false,
        isLoadingStreaks: true,
        streakLoadError: false,
        garminPermissions: null,
        isRefreshingGarminUserId: false,
        // null while loading, undefined if the lookup failed, otherwise the
        // provider list from the server ([] means email/password).
        linkedAuthProviders: null,
        memberMemories: null,
        coachConversations: null,
        scheduleDays: null
      }
      this.reloadAllMemberData()
    }
    this.loadMember = this.loadMember.bind(this)
    this.loadAllAchievements()
  }

  reloadAllMemberData = async () => {
    this.loadMember()
    this.loadWorkouts()
    this.loadAchievements()
    this.loadDynamicIntervals()
    this.loadLibrary()
    this.getReferralStats()
    this.loadSubscriptionExtensions()
    this.loadStreaks()
    this.loadCoachInfo()
    this.loadUserRole() // This now handles coach status checking
    this.loadMemories()
    this.loadCoachConversations()
    this.loadLinkedAuthProviders()
    this.loadScheduleDays()
    // loadGarminPermissions is called from loadMember callback
  }

  // `authData` is stripped from every _User this dashboard queries (parse-server only returns it
  // to the master key or to the user themselves), so linked providers have to come from the
  // server. Without this the page cannot tell an Apple or Google account from an email one.
  loadLinkedAuthProviders = async () => {
    const id = this.props.id || this.props.match.params.id
    if (!id) return

    this.setState({ linkedAuthProviders: null })
    try {
      const result = await Parse.Cloud.run('getLinkedAuthMethods', { userId: id })
      // Only an actual array is an answer. Coercing anything else to [] would render
      // "Email/Password", which is the wrong-answer-instead-of-no-answer bug this page
      // already had.
      if (!Array.isArray(result?.providers)) {
        throw new Error(`unexpected response shape: ${JSON.stringify(result)}`)
      }
      this.setState({ linkedAuthProviders: result.providers })
    } catch (error) {
      console.error('Error loading linked auth methods: ' + error.message)
      this.setState({ linkedAuthProviders: undefined })
    }
  }

  loadUserRole = async () => {
    const id = this.props.id || this.props.match.params.id
    if (!id) return

    try {
      // Query the _Role class and see which roles this user belongs to
      const roleQuery = new Parse.Query('_Role')
      roleQuery.equalTo('users', {
        __type: 'Pointer',
        className: '_User',
        objectId: id
      })
      const roles = await roleQuery.find()
      // A user can have multiple roles, so store them in an array
      const userRoles = []
      // Check for each possible role
      if (roles.some(role => role.get('name') === 'Admin')) {
        userRoles.push('Admin')
      }
      if (roles.some(role => role.get('name') === 'Coach')) {
        userRoles.push('Coach')
      }
      // Update state and then update coach status based on roles
      this.setState({ userRoles }, () => {
        this.updateCoachStatusFromRole()
      })
    } catch (error) {
      console.error('Error fetching user role:', error.message)
    }
  }

  // Using userRoles instead of separate cloud function
  updateCoachStatusFromRole = () => {
    const isCoach = this.state.userRoles.includes('Coach')

    if (isCoach !== this.state.isCoach) {
      this.setState({ isCoach })

      // If user is a coach, fetch their assigned users
      if (isCoach) {
        this.loadAssignedUsers()
      } else {
        this.setState({ assignedUsers: [] })
      }
    }
  }

  loadAssignedUsers = async () => {
    const id = this.props.id || this.props.match.params.id
    if (!id) return

    try {
      const result = await Parse.Cloud.run('getAcademyCoachUsers', { coachId: id })
      this.setState({ assignedUsers: result.users || [] })
    } catch (error) {
      console.error('Error fetching assigned users:', error.message)
      this.setState({ assignedUsers: [] })
    }
  }

  loadCoachInfo = async () => {
    const id = this.props.id || this.props.match.params.id
    if (!id) return

    try {
      const coachInfo = await Parse.Cloud.run('getUserAcademyCoach', { userId: id })
      this.setState({ coachInfo: coachInfo?.coach })
    } catch (error) {
      console.error('Error loading coach info:', error.message)
    }
  }

  loadGarminPermissions = async () => {
    const id = this.props.id || this.props.match.params.id
    if (!id) return

    // Only fetch permissions if user has a Garmin token
    if (!hasGarminLink(this.state.member)) {
      this.setState({ garminPermissions: null })
      return
    }

    try {
      const permissions = await Parse.Cloud.run('getGarminUserPermissions', { userId: id })
      this.setState({ garminPermissions: permissions })
    } catch (error) {
      console.error('Error loading Garmin permissions:', error.message)
      this.setState({ garminPermissions: null })
    }
  }

  handleRefreshGarminUserId = async (event) => {
    event.preventDefault()
    this.setState({ isRefreshingGarminUserId: true })
    try {
      const res = await Parse.Cloud.run('refreshGarminUserId', {
        userId: this.state.member.id
      })
      // Re-read the member so the field shows what was actually stored.
      await this.loadMember()
      if (res?.garminUserId) {
        window.alert('Garmin User ID: ' + res.garminUserId)
      } else {
        window.alert('Garmin returned no User ID for this member.')
      }
    } catch (error) {
      window.alert('Could not fetch the Garmin User ID: ' + error.message)
    } finally {
      this.setState({ isRefreshingGarminUserId: false })
    }
  }

  getGarminStatus = () => {
    if (!hasGarminLink(this.state.member)) {
      return 'not connected'
    }

    // If we have a token but no permissions data yet, show loading
    if (this.state.garminPermissions === null) {
      return 'checking...'
    }

    // Check if all required permissions are granted
    const requiredPermissions = ['ACTIVITY_EXPORT', 'WORKOUT_IMPORT']
    const hasAllPermissions = requiredPermissions.every(permission =>
      this.state.garminPermissions.includes(permission)
    )

    return hasAllPermissions ? 'connected' : 'reconnect required'
  }

  getLinkedAuthMethods = () => {
    const providers = this.state.linkedAuthProviders
    if (providers === null) return ['Loading...']
    if (providers === undefined) return ['Failed to load']
    // No linked provider means there is nothing to log in with but email/password.
    if (providers.length === 0) return ['Email/Password']

    return providers.map((provider) => AUTH_PROVIDER_LABELS[provider] || provider)
  }

  getLastLoginMethod = () => {
    const loginMethod = this.state.userInfo?.get('loginMethod')
    if (!loginMethod || loginMethod === 'none') return null

    return AUTH_PROVIDER_LABELS[loginMethod] || loginMethod
  }

  loadStreaks = async () => {
    const id = this.props.id || this.props.match.params.id
    if (!id) {
      this.setState({ isLoadingStreaks: false })
      return
    }
    // Re-show loading + clear any prior error on each (re)load, e.g. via reloadAllMemberData().
    this.setState({ isLoadingStreaks: true, streakLoadError: false })
    try {
      const workoutResults = await Parse.Cloud.run('getGuidedWorkoutStreakCount', { userId: id })
      this.setState({
        workoutStreak: workoutResults?.streakCountWeeks,
        workoutsThisWeek: workoutResults?.numWorkoutsCompletedThisWeek
      })
      const totdResults = await Parse.Cloud.run('getTipOfTheDayStreakCount', { userId: id })
      this.setState({
        totdStreak: totdResults?.streakCountDays
      })
    } catch (error) {
      console.error('Error loading streaks: ' + error.message)
      this.setState({ streakLoadError: true })
    } finally {
      this.setState({ isLoadingStreaks: false })
    }
  }

  workoutStreakLabel = () => {
    // null = just reset. Prioritize it so a follow-up reload doesn't flip the label to 'Loading...'.
    if (this.state.workoutStreak === null) return 'Cache cleared - recomputes on next open'
    if (this.state.isLoadingStreaks) return 'Loading...'
    // undefined = never loaded (distinct from a real 0); reset/recalc set an explicit value.
    if (this.state.workoutStreak === undefined) return this.state.streakLoadError ? 'Failed to load' : 'Loading...'
    return `${this.state.workoutStreak} weeks (${this.state.workoutsThisWeek || 0} this week)`
  }

  totdStreakLabel = () => {
    if (this.state.isLoadingStreaks) return 'Loading...'
    if (this.state.totdStreak === undefined) return this.state.streakLoadError ? 'Failed to load' : 'Loading...'
    return `${this.state.totdStreak} days`
  }

  handleResetStreakCache = async (event) => {
    event.preventDefault()
    const confirm = window.confirm('Reset this user\'s streak cache? The value is cleared now and recomputes on their next app open (use Recalculate Now to recompute immediately).')
    if (!confirm) return
    this.setState({ isResettingStreak: true })
    try {
      await Parse.Cloud.run('adminInvalidateStreakCache', { userId: this.state.member.id })
      // Don't reload: getGuidedWorkoutStreakCount would recompute and repopulate the
      // cache, defeating the reset. Clear the local display; it recomputes on next fetch.
      this.setState({ workoutStreak: null, workoutsThisWeek: null })
      window.alert('Streak cache reset. It will recompute on the member\'s next app open.')
    } catch (error) {
      window.alert('Error resetting streak cache: ' + error.message)
    } finally {
      this.setState({ isResettingStreak: false })
    }
  }

  handleRecalculateStreak = async (event) => {
    event.preventDefault()
    const confirm = window.confirm('Recalculate this user\'s streak now? This clears the cache and recomputes from scratch.')
    if (!confirm) return
    this.setState({ isRecalculatingStreak: true })
    try {
      const res = await Parse.Cloud.run('adminRecalculateStreak', { userId: this.state.member.id })
      this.setState({
        workoutStreak: res?.streakCountWeeks,
        workoutsThisWeek: res?.numWorkoutsCompletedThisWeek
      })
      window.alert('Streak recalculated: ' + (res?.streakCountWeeks || 0) + ' weeks')
    } catch (error) {
      window.alert('Error recalculating streak: ' + error.message)
    } finally {
      this.setState({ isRecalculatingStreak: false })
    }
  }

  loadAllAchievements = async () => {
    const query = new Parse.Query('Achievement')
    const allAchievements = await query.find()
    this.setState({
      allAchievements: allAchievements
    })
  }

  loadSubscriptionExtensions = async () => {
    const results = await Parse.Cloud.run('getSubscriptionExtensionsForUser', { userId: this.props.match.params.id })
    this.setState({
      subscriptionExtensions: results?.extensions || []
    })
  }

  loadMember = async () => {
    const id = this.props.id || this.props.match.params.id
    if (!id) {
      alert(
        'Error: No member id. URL format: https://admin.myswimpro.com/#/members/id'
      )
      return
    }

    const memberType = new Parse.Object('_User')
    const query = new Parse.Query(memberType)
    query.include([
      'userInfo_v3',
      'userInfo_v3.trainingPlanHistory',
      'settings',
      'userTraining'
    ])
    const results = await query.get(id)
    if (!results) {
      alert('No member found for id ' + id)
      return
    }
    this.setState({
      member: results,
      userInfo: results.get('userInfo_v3'),
      settings: results.get('settings'),
      training: results.get('userTraining')
    }, () => {
      // Load Garmin permissions after member is loaded
      this.loadGarminPermissions()
    })
  }

  // setScheduleDays edits the member's newest ScheduleDays row, not the one
  // Settings.swimDays points at, so read the row the app actually updates.
  loadScheduleDays = async () => {
    const id = this.props.id || this.props.match.params.id
    if (!id) return
    try {
      const query = new Parse.Query('ScheduleDays')
      query.equalTo('userId', id)
      query.descending('updatedAt')
      const scheduleDays = await query.first()
      this.setState({ scheduleDays: scheduleDays || null })
    } catch (error) {
      console.error('Error loading swim days: ' + error.message)
    }
  }

  unlinkAllAuthMethodsConfirm = async (event) => {
    event.preventDefault()
    const confirm = window.confirm('Are you sure you want to delete all auth methods? This user will need to use email/password to login.')
    if (!confirm) return
    await this.unlinkAllAuthMethods()
    // The badges above are now stale: unlinking is exactly what changes them.
    await this.loadLinkedAuthProviders()
  }

  unlinkAllAuthMethods = async (inputUser) => {
    const user = inputUser || this.state.member
    console.log('removing auth methods for ' + user.id)
    try {
      await Parse.Cloud.run('unlinkAllAuthMethods', { userId: user.id })
      window.alert('Auth methods removed.')
    } catch (error) {
      window.alert('Error unlinking auth methods: ' + error.message)
    }
  }

  handleAssignCoach = async (event) => {
    event.preventDefault()
    const coachId = window.prompt('Enter the coach user ID to assign this member to:')
    if (!coachId || !coachId.length) return

    try {
      await Parse.Cloud.run('assignAcademyCoach', {
        userId: this.state.member.id,
        coachId: coachId
      })
      window.alert('Member successfully assigned to coach.')
      // Load the updated coach information
      await this.loadCoachInfo()
    } catch (error) {
      window.alert('Error assigning coach: ' + error.message)
    }
  }

  handleToggleCoachRole = async (event) => {
    event.preventDefault()

    try {
      if (this.state.isCoach) {
        // Remove coach role
        const confirm = window.confirm('Are you sure you want to remove coach privileges from this user?')
        if (!confirm) return

        await Parse.Cloud.run('removeUserAcademyCoach', {
          userId: this.state.member.id
        })
        // Update roles
        const updatedRoles = [...this.state.userRoles].filter(role => role !== 'Coach')
        this.setState({
          userRoles: updatedRoles
        }, () => {
          this.updateCoachStatusFromRole()
        })
        window.alert('Coach privileges removed successfully.')
      } else {
        // Make user a coach
        const confirm = window.confirm('Are you sure you want to promote this user to Academy Coach?')
        if (!confirm) return

        await Parse.Cloud.run('makeUserAcademyCoach', {
          userId: this.state.member.id
        })
        // Update roles - add Coach role if not already present
        const updatedRoles = [...this.state.userRoles]
        if (!updatedRoles.includes('Coach')) {
          updatedRoles.push('Coach')
        }
        this.setState({
          userRoles: updatedRoles
        }, () => {
          this.updateCoachStatusFromRole()
        })
        window.alert('User promoted to Academy Coach successfully.')
      }

      // No need for additional refresh since we updated the state above
    } catch (error) {
      window.alert('Error toggling coach role: ' + error.message)
    }
  }

  handleCreateAcademyChat = async (event) => {
    event.preventDefault()

    this.setState({ isCreatingWelcomeChat: true })

    try {
      await Parse.Cloud.run('createAcademyChatDefaultChannel', {
        userId: this.state.member.id
      })
      window.location.reload()
    } catch (error) {
      this.setState({ isCreatingWelcomeChat: false })
      window.alert('Error creating academy chat: ' + error.message)
    }
  }

  handleCreateEmptyAcademyChat = async (event) => {
    event.preventDefault()

    this.setState({ isCreatingEmptyChat: true })

    try {
      await Parse.Cloud.run('createAcademyChatDefaultChannel', {
        userId: this.state.member.id,
        isEmpty: true
      })
      window.location.reload()
    } catch (error) {
      this.setState({ isCreatingEmptyChat: false })
      window.alert('Error creating empty academy chat: ' + error.message)
    }
  }

  loadWorkouts = () => {
    const id = this.props.id || this.props.match.params.id
    if (!id) {
      return
    }

    const user = new Parse.Object('_User')
    user.id = id

    const workoutType = new Parse.Object('CompletedWorkout_v3')
    const query = new Parse.Query(workoutType)
    query.include('workout')
    query.descending('dateCompleted')
    query.equalTo('user', user)
    query.limit(1000)
    query.find().then(
      (results) => {
        if (!results) {
          return
        }
        this.setState({
          workouts: results
        })
      },
      (error) => {
        alert('Error getting member: ' + error.message)
      }
    )
  }

  loadAchievements = () => {
    const id = this.props.id || this.props.match.params.id
    if (!id) {
      return
    }

    const achievementType = new Parse.Object('Achievement_Unlocked')
    const query = new Parse.Query(achievementType)
    query.include('achievement')
    query.descending('dateCompleted')
    query.equalTo('user_id', id)
    query.limit(1000)
    query.find().then(
      (results) => {
        if (!results) {
          return
        }
        console.log(
          '[MemberDetailComponent] Got ' + results.length + ' members.'
        )
        this.setState({
          achievements: results
        })
      },
      (error) => {
        alert('Error getting member: ' + error.message)
      }
    )
  }

  loadDynamicIntervals = () => {
    Parse.Cloud.run('loadTimes_v4', {
      userId: this.props.match.params.id,
      freestyleDistancePTI: 100
    }).then(
      (results) => {
        this.setState({
          dynamicIntervals: results
        })
      },
      (error) => {
        alert('Error getting dynamic intervals: ' + error.message)
      }
    )
  }

  handleAdTrackingChange = async (event) => {
    event.preventDefault()
    const user = this.state.member
    if (user) {
      Parse.Cloud.run('updatePrivacyPreferences', { userId: user.id }).then(
        (results) => {
          this.setState({
            member: user
          })
        },
        (error) => {
          alert('Error updating privacy status: ' + error.message)
        }
      )
    } else {
      console.log('[handleEliteOverrideChange] No user to edit')
    }
  }

  numberOfCompletedWorkoutsText = () => {
    if (!this.state.workouts) return ''
    if (this.state.workouts.length === 1000) return '1000+'
    return '' + this.state.workouts.length
  }

  handleAdminNoteEdit = (event) => {
    event.preventDefault()
    if (this.state.isEditingAdminNotes) {
      // save
      this.state.userInfo.set('adminNotes', this.state.tempAdminNotes)
      this.state.userInfo.save().then(
        () => {
          this.setState({
            isEditingAdminNotes: false
          })
        },
        (error) => {
          window.alert(error)
        }
      )
    } else {
      this.setState({
        tempAdminNotes: this.state.userInfo.get('adminNotes'),
        isEditingAdminNotes: true
      })
    }
  }

  handleAdminNoteChange = (event) => {
    this.setState({
      tempAdminNotes: event.target.value
    })
  }

  handleNewAward = async (event) => {
    event.preventDefault()
    const newAwardId = window.prompt(
      "Enter Achivement ID. You can find this on the Achievement's detail page"
    )
    if (!newAwardId || !newAwardId.length) return
    const achievement = this.state.allAchievements.find(
      (obj) => obj.id === newAwardId
    )
    if (!achievement) {
      window.alert('ERROR: Invalid Achievement ID')
      return
    }
    if (this.state.achievements.find((obj) => obj.id === newAwardId)) {
      window.alert('ERROR: Achievement already awarded.')
      return
    }

    const achivementUnlocked = new Parse.Object('Achievement_Unlocked')
    achivementUnlocked.set('user_id', this.state.member.id)
    achivementUnlocked.set('achievement', achievement)
    await achivementUnlocked.save()
    await this.loadAchievements()
  }

  handleDeleteAccount = (event) => {
    event.preventDefault()

    const confirmId = window.prompt(
      "WARNING!\n\nDeleting a member's account requires explict permission from the member who must use the email we have on file to contact us.\n\nThis action cannot be undone and deletes all member data, including workouts. To confirm, please enter the member id:",
      ''
    )

    if (!confirmId) return
    if (confirmId !== this.state.member.id) {
      window.alert('Error: Incorrect ID entered')
      return
    }

    const params = {
      objectId: this.state.member.id,
      adminOverride: true // Deprecated
    }
    Parse.Cloud.run('deleteAccount_v4', params).then(
      (results) => {
        window.location.reload(false)
      },
      (error) => {
        window.alert('Error deleting account: ' + error.message)
      }
    )
  }

  handleEditUsername = (event) => {
    event.preventDefault()

    const newEmail = window.prompt('Please enter a new email.')
    if (!newEmail || newEmail === '') return

    const params = {
      userId: this.state.member.id,
      email: newEmail.toLowerCase()
    }
    Parse.Cloud.run('updateEmail', params).then(
      (results) => {
        this.reloadAllMemberData()
      },
      (error) => {
        window.alert('Error changing email: ' + error.message)
      }
    )
  }

  handleEditFirstName = (event) => {
    event.preventDefault()

    const newValue = window.prompt('Please enter a new first name.')
    if (!newValue || newValue === '') return

    this.state.userInfo.set('firstName', newValue)
    this.state.userInfo.save().then(
      () => {
        this.setState({
          userInfo: this.state.userInfo
        })
      },
      (error) => {
        window.alert('Error saving: ' + error.message)
      }
    )
  }

  handleEditLastName = (event) => {
    event.preventDefault()

    const newValue = window.prompt('Please enter a new last name.')
    if (!newValue || newValue === '') return

    this.state.userInfo.set('lastName', newValue)
    this.state.userInfo.save().then(
      () => {
        this.setState({
          userInfo: this.state.userInfo
        })
      },
      (error) => {
        window.alert('Error saving: ' + error.message)
      }
    )
  }

  handleEditTotalTime = (event) => {
    event.preventDefault()

    const newValue = window.prompt('Please enter a new total time, in minutes.')
    if (!newValue || newValue === '') return
    const newNumber = parseInt(newValue)

    this.state.userInfo.set('totalTime', newNumber * 60)
    this.state.userInfo.save().then(
      () => {
        this.setState({
          userInfo: this.state.userInfo
        })
      },
      (error) => {
        window.alert('Error saving: ' + error.message)
      }
    )
  }

  handleEditTotalDistance = (event) => {
    event.preventDefault()

    const newValue = window.prompt(
      'Please enter a new total distance, in meters.'
    )
    if (!newValue || newValue === '') return
    const newNumber = parseInt(newValue)

    this.state.userInfo.set('totalDistance', newNumber)
    this.state.userInfo.save().then(
      () => {
        this.setState({
          userInfo: this.state.userInfo
        })
      },
      (error) => {
        window.alert('Error saving: ' + error.message)
      }
    )
  }

  handleEditTotalWorkouts = (event) => {
    event.preventDefault()

    const newValue = window.prompt(
      'Please enter a new total number of workouts.'
    )
    if (!newValue || newValue === '') return
    const newNumber = parseInt(newValue)

    this.state.userInfo.set('totalWorkoutsCompleted', newNumber)
    this.state.userInfo.save().then(
      () => {
        this.setState({
          userInfo: this.state.userInfo
        })
      },
      (error) => {
        window.alert('Error saving: ' + error.message)
      }
    )
  }

  renderLoginMethodBadges = () => this.getLinkedAuthMethods().map((method) => {
    let badgeClass = 'bg-secondary'
    if (method === 'Apple') badgeClass = 'text-bg-dark'
    else if (method === 'Google') badgeClass = 'bg-danger'
    else if (method === 'Facebook') badgeClass = 'bg-primary'

    return (
      <span
        key={method}
        className={`badge me-1 ${badgeClass}`}
      >
        {method}
      </span>
    )
  })

  getMemberName = () => {
    const userInfo = this.state.userInfo
    return [userInfo?.get('firstName'), userInfo?.get('lastName')]
      .filter(Boolean).join(' ')
  }

  renderHeader = () => {
    const { member, settings } = this.state
    const garminStatus = this.getGarminStatus()
    const integrationBadges = [
      [garminStatus !== 'not connected' ? 'Garmin: ' + garminStatus : null,
        garminStatus === 'connected' ? 'text-bg-success' : 'text-bg-warning'],
      [member.get('stravaAccessToken') ? 'Strava' : null, 'text-bg-success'],
      [member.get('trainingPeaksAccessToken') ? 'TrainingPeaks' : null, 'text-bg-success'],
      [settings?.get('isPrivate') ? 'Private profile' : null, 'text-bg-light border'],
      [member.get('isAdTrackingDisabled') ? 'Ad tracking off' : null, 'text-bg-light border']
    ].filter(([label]) => label)
    return (
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <h1 className="mb-1">{this.getMemberName() || 'Unnamed member'}</h1>
          <div className="text-muted">
            {member.get('username')}
            {' · '}
            <code>{member.id}</code>
            {' · '}
            Member since {formatDate(member.createdAt)}
          </div>
          <div className="mt-2">
            {this.state.userRoles.map(role => (
              <span
                key={role}
                className={`badge me-1 ${role === 'Admin' ? 'bg-danger' : 'bg-primary'}`}
              >
                {role}
              </span>
            ))}
            {this.renderLoginMethodBadges()}
            {integrationBadges.map(([label, className]) => (
              <span key={label} className={'badge me-1 ' + className}>{label}</span>
            ))}
          </div>
        </div>
        <div className="d-flex gap-2">
          <a className="btn btn-sm btn-secondary" href={`/#/workouts/new?generateWorkoutUserId=${member.id}`}>
            🧪 Generate Workout
          </a>
          <a className="btn btn-sm btn-primary"
            href={'https://app.revenuecat.com/projects/' + Config.revenueCatProjectId + '/customers/' + member.id}
            target='_blank' rel='noreferrer'>
            RevenueCat
          </a>
        </div>
      </div>
    )
  }

  // Present-only list of fields the apps no longer write, so old data stays
  // findable without crowding the sections above.
  renderLegacyFields = (object, fields) => {
    if (!object) {
      return null
    }
    const present = fields.filter(([key]) => !isBlank(object.get(key)))
    if (present.length === 0) {
      return null
    }
    return (
      <DetailMore label="Legacy fields" count={present.length}>
        <DetailGrid>
          {present.map(([key, label]) => (
            <DetailFieldComponent key={key} label={label} source={object.className + '.' + key}
              value={describeRawValue(key, object.get(key))} />
          ))}
        </DetailGrid>
      </DetailMore>
    )
  }

  renderAccountSection = () => {
    const { member } = this.state
    return (
      <DetailSectionComponent id="member-account" title="Account" subtitle={member.id}
        collapsible defaultOpen>
        <DetailGrid>
          <DetailFieldComponent label="Username" source="_User.username"
            action={<a href="" onClick={this.handleEditUsername}>Edit</a>}>
            <span className="bg-light detail-pre">{member.get('username')}</span>
          </DetailFieldComponent>
          <DetailFieldComponent label="Email" source="_User.email" value={member.get('email')} />
          <DetailFieldComponent label="Phone" source="_User.phoneNumber">
            {member.get('phoneNumber')
              ? <a href={`tel:${member.get('phoneNumber')}`}>{member.get('phoneNumber')}</a>
              : null}
          </DetailFieldComponent>
          <DetailFieldComponent label="Created" source="_User.createdAt" value={formatDateTime(member.createdAt)} />
          <DetailFieldComponent label="Updated" source="_User.updatedAt" value={formatDateTime(member.updatedAt)} />
          <DetailFieldComponent label="Last login method" source="UserInfo_v3.loginMethod"
            value={this.getLastLoginMethod()} />
          <DetailFieldComponent label="Login methods" source="_User.authData (getLinkedAuthMethods)"
            action={<a href="" onClick={this.unlinkAllAuthMethodsConfirm} className="text-danger">Unlink all</a>}>
            <span>{this.renderLoginMethodBadges()}</span>
          </DetailFieldComponent>
          <DetailFieldComponent label="Transactional emails" source="_User.isTransactionalEmailsUnsubscribed"
            value={member.get('isTransactionalEmailsUnsubscribed') ? 'Unsubscribed' : 'Allowed'} />
          <DetailFieldComponent label="Ad tracking" source="_User.isAdTrackingDisabled"
            action={<a href="" onClick={this.handleAdTrackingChange}>Flip</a>}
            value={member.get('isAdTrackingDisabled') === undefined
              ? 'Not set'
              : member.get('isAdTrackingDisabled') ? 'Opted-out' : 'Allowed'} />
          <DetailFieldComponent label="Last password reset link" source="_User.lastPasswordResetLink"
            value={member.get('lastPasswordResetLink')} wide />
        </DetailGrid>
        <DetailMore label="Marketing & analytics">
          <DetailGrid>
            <DetailFieldComponent label="Acquisition channel" source="_User.acquisitionChannel"
              value={member.get('acquisitionChannel')} />
            <DetailFieldComponent label="Last Amplitude session" source="_User.lastAmplitudeSessionId"
              value={member.get('lastAmplitudeSessionId')
                ? `${member.get('lastAmplitudeSessionId')} (${formatDateTime(member.get('lastAmplitudeSessionId'))})`
                : null} />
            <DetailFieldComponent label="ClickFunnels contact" source="_User.clickfunnelsContactId"
              value={member.get('clickfunnelsContactId')} />
            <DetailFieldComponent label="ClickFunnels public ID" source="_User.clickfunnelsPublicId"
              value={member.get('clickfunnelsPublicId') || member.get('clickfunnels_public_id')} />
            <DetailFieldComponent label="GoHighLevel contact" source="_User.gohighlevelContactId"
              value={member.get('gohighlevelContactId')} />
            <DetailFieldComponent label="First-workout analysis email" source="_User.didSendFirstWorkoutAnalysisEmail"
              value={formatBool(member.get('didSendFirstWorkoutAnalysisEmail'))} />
            <DetailFieldComponent label="Last account export" source="_User.lastSavedAccountDataKey (S3 key)"
              value={member.get('lastSavedAccountDataKey')} wide />
          </DetailGrid>
        </DetailMore>
        {this.renderLegacyFields(member, USER_LEGACY_FIELDS)}
      </DetailSectionComponent>
    )
  }

  renderProfileSection = () => {
    const { member, userInfo, settings } = this.state
    const weight = settings?.get('weight')
    const picture = userInfo?.get('profilePicture')
    const age = describeAge(settings?.get('yearBorn'), settings?.get('monthBorn'))
    const isPrivate = settings?.get('isPrivate') || userInfo?.get('private')
    return (
      <DetailSectionComponent id="member-profile" title="Profile" collapsible
        subtitle={joinSummary([
          age && 'age ' + age,
          settings?.get('gender'),
          isPrivate && 'private profile'
        ])}>
        {!userInfo && <p className="text-danger">No UserInfo_v3 linked</p>}
        <DetailGrid>
          <DetailFieldComponent label="First name" source="UserInfo_v3.firstName"
            action={userInfo && <a href="" onClick={this.handleEditFirstName}>Edit</a>}
            value={userInfo?.get('firstName')} />
          <DetailFieldComponent label="Last name" source="UserInfo_v3.lastName"
            action={userInfo && <a href="" onClick={this.handleEditLastName}>Edit</a>}
            value={userInfo?.get('lastName')} />
          <DetailFieldComponent label="Age" source="Settings.yearBorn + monthBorn"
            value={age} />
          <DetailFieldComponent label="Born" source="Settings.yearBorn / monthBorn"
            value={settings?.get('yearBorn')
              ? (settings.get('monthBorn') ? settings.get('monthBorn') + '/' : '') + settings.get('yearBorn')
              : null} />
          <DetailFieldComponent label="Gender" source="Settings.gender" value={settings?.get('gender')} />
          <DetailFieldComponent label="Weight" source="Settings.weight.currentInKg"
            value={describeWeight(weight?.currentInKg, weight?.displayUnit)} />
          <DetailFieldComponent label="Target weight" source="Settings.weight.targetInKg / targetDate"
            value={weight?.targetInKg !== undefined
              ? describeWeight(weight.targetInKg, weight.displayUnit) +
                (weight.targetDate ? ' by ' + formatDate(weight.targetDate) : '')
              : null} />
          <DetailFieldComponent label="UTC offset" source="Settings.secondsFromGMT"
            value={settings?.get('secondsFromGMT') !== undefined ? formatUtcOffset(settings.get('secondsFromGMT')) : null} />
          <DetailFieldComponent label="Private profile" source="Settings.isPrivate or UserInfo_v3.private (either one makes it private)"
            value={settings || userInfo ? (isPrivate ? 'Yes' : 'No') : null} />
          <DetailFieldComponent label="Profile picture" source="UserInfo_v3.profilePicture">
            {picture
              ? <a href={typeof picture === 'string' ? picture : picture.url()} target="_blank" rel="noopener noreferrer">Open</a>
              : null}
          </DetailFieldComponent>
          <DetailFieldComponent label="Bio" source="UserInfo_v3.bio" wide>
            {userInfo?.get('bio') ? <span className="detail-pre-line">{userInfo.get('bio')}</span> : null}
          </DetailFieldComponent>
          {member.get('bio') && (
            <DetailFieldComponent label="Bio (account)" source="_User.bio (set by updateUser; separate from UserInfo_v3.bio)" wide>
              <span className="detail-pre-line">{member.get('bio')}</span>
            </DetailFieldComponent>
          )}
        </DetailGrid>
        <div className="detail-subheading">Social</div>
        <DetailGrid>
          <DetailFieldComponent label="Followers" source="UserInfo_v3.followers"
            value={userInfo?.get('followers') ? formatNumber(userInfo.get('followers').length, 0) : null} />
          <DetailFieldComponent label="Following" source="UserInfo_v3.following"
            value={userInfo?.get('following') ? formatNumber(userInfo.get('following').length, 0) : null} />
          <DetailFieldComponent label="Facebook ID" source="UserInfo_v3.facebookId"
            value={userInfo?.get('facebookId')} />
          <DetailFieldComponent label="Referral code" source="getUserReferralCode">
            {this.state.referralCode
              ? <a href={'https://myswimpro.com/refer?code=' + this.state.referralCode}>{this.state.referralCode}</a>
              : null}
          </DetailFieldComponent>
          <DetailFieldComponent label="Referrals" source="getCouponCodeRedemptionCount"
            value={'' + (this.state.referralCount || 0)} />
        </DetailGrid>
        {this.renderLegacyFields(userInfo, USER_INFO_PROFILE_LEGACY_FIELDS)}
      </DetailSectionComponent>
    )
  }

  renderIntegrationsSection = () => {
    const { member, settings, garminPermissions } = this.state
    const hasGarmin = hasGarminLink(member)
    return (
      <DetailSectionComponent id="member-integrations" title="Integrations" collapsible
        subtitle={joinSummary([
          hasGarmin && 'Garmin ' + this.getGarminStatus(),
          member.get('stravaAccessToken') && 'Strava',
          member.get('trainingPeaksAccessToken') && 'TrainingPeaks'
        ]) || 'none linked'}>
        <div className="detail-subheading mt-0">Garmin</div>
        <DetailGrid>
          <DetailFieldComponent label="Status" source="_User.garminAccessToken or garminOAuth2AccessToken + getGarminUserPermissions"
            value={this.getGarminStatus()} />
          <DetailFieldComponent label="Permissions" source="getGarminUserPermissions"
            value={Array.isArray(garminPermissions) ? joinList(garminPermissions) : null} />
          <DetailFieldComponent label="Garmin User ID" source="_User.garminUserId"
            value={member.get('garminUserId') || (hasGarmin ? 'not captured' : null)} />
          <DetailFieldComponent label="Linked" source="_User.garminLinkedAt"
            value={member.get('garminLinkedAt') ? formatDateTime(member.get('garminLinkedAt')) : null} />
          <DetailFieldComponent label="Last push from Garmin" source="_User.garminLastPushAt"
            value={member.get('garminLastPushAt') ? formatDateTime(member.get('garminLastPushAt')) : null} />
          <DetailFieldComponent label="Token expired" source="_User.garminTokenExpiredAt"
            value={member.get('garminTokenExpiredAt') ? formatDateTime(member.get('garminTokenExpiredAt')) : null} />
          <DetailFieldComponent label="OAuth 2 token" source="_User.garminOAuth2AccessToken / garminOAuth2ExpiresAt"
            value={member.get('garminOAuth2AccessToken')
              ? 'set' + (member.get('garminOAuth2ExpiresAt') ? ', expires ' + formatDateTime(member.get('garminOAuth2ExpiresAt')) : '')
              : null} />
          <DetailFieldComponent label="Cap workouts at 50 steps" source="Settings.legacyGarminCompatibility"
            value={describeSettingsFlag(settings, 'legacyGarminCompatibility')} />
          <DetailFieldComponent label="Keep last rest step" source="Settings.garminKeepLastRestStep"
            value={describeSettingsFlag(settings, 'garminKeepLastRestStep')} />
        </DetailGrid>
        {hasGarmin && (
          <div className="mt-2">
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={this.handleRefreshGarminUserId}
              disabled={this.state.isRefreshingGarminUserId}
              title="Asks Garmin for this member's API User ID and stores it. Needed before the OAuth 2 migration."
            >
              {this.state.isRefreshingGarminUserId ? 'Fetching...' : 'Fetch User ID from Garmin'}
            </button>
            <GarminHistoryBackfillComponent
              memberId={member.id}
              garminPermissions={garminPermissions}
            />
          </div>
        )}
        <div className="detail-subheading">Other services</div>
        <DetailGrid>
          <DetailFieldComponent label="Strava" source="_User.stravaAccessToken"
            value={member.get('stravaAccessToken') ? 'authorized' : 'not linked'} />
          <DetailFieldComponent label="TrainingPeaks" source="_User.trainingPeaksAccessToken"
            value={member.get('trainingPeaksAccessToken') ? 'authorized' : 'not linked'} />
        </DetailGrid>
      </DetailSectionComponent>
    )
  }

  describeMemberPool = (pool) => pool.isDisplayInYards
    ? `${Math.round(pool.distanceInMeters * 1.09361 * 100) / 100}yd`
    : `${pool.distanceInMeters}m`

  renderPools = () => {
    const pools = this.state.member.get('pools') || []
    if (pools.length === 0) {
      return null
    }
    return (
      <ul className="mb-0 ps-3">
        {pools.map((pool, index) => (
          <li key={index}>
            {this.describeMemberPool(pool)}
            {pool.isDefault ? ' (default)' : ''}
          </li>
        ))}
      </ul>
    )
  }

  renderStrokes = () => {
    const strokes = this.state.settings?.get('swimStrokes')
    if (!Array.isArray(strokes) || strokes.length === 0) {
      return null
    }
    return (
      <span>
        {strokes.map((isEnabled, index) => (
          <span key={index} className={'badge me-1 ' + (isEnabled ? 'text-bg-success' : 'text-bg-light border text-decoration-line-through')}>
            {getStrokeString(index)}
          </span>
        ))}
      </span>
    )
  }

  renderHeartRateZones = (zones) => {
    if (!zones) {
      return this.state.settings ? 'Default (220 - age, 60/70/80/90%)' : null
    }
    const maxes = [zones.zone1Max, zones.zone2Max, zones.zone3Max, zones.zone4Max]
    return `${zones.mode || 'unknown'} · max ${zones.maxHR ?? '?'} bpm · zones 1-4 end below ${maxes.map((value) => value ?? '?').join(' / ')}`
  }

  renderPreferencesSection = () => {
    const { member, settings, scheduleDays } = this.state
    const goalList = (goals) => joinList((goals || []).map(getGoalName))
    const defaultPool = this.getDefaultPool()
    return (
      <DetailSectionComponent id="member-preferences" title="Swim preferences" collapsible
        subtitle={joinSummary([
          defaultPool && this.describeMemberPool(defaultPool) + ' pool',
          typeof member.get('preferredWorkoutLength') === 'number' &&
            formatNumber(member.get('preferredWorkoutLength'), 0) + ' per workout',
          joinList(activeSwimDays(scheduleDays))
        ])}>
        {!settings && <p className="text-danger">No Settings linked</p>}
        <DetailGrid>
          <DetailFieldComponent label="Pools" source="_User.pools">{this.renderPools()}</DetailFieldComponent>
          <DetailFieldComponent label="Preferred workout length" source="_User.preferredWorkoutLength"
            value={getPreferredWorkoutLengthDisplay(member)} />
          <DetailFieldComponent label="Skill level (deprecated)" source="_User.skillLevel (only a fallback workout length now)"
            value={describeEnum(SKILL_LEVEL_LABELS, member.get('skillLevel'))} />
          <DetailFieldComponent label="Swim skill" source="Settings.swimSkillLevel"
            value={settings?.get('swimSkillLevel')} />
          <DetailFieldComponent label="Strength level" source="Settings.strengthLevel"
            value={describeEnum(SKILL_LEVEL_LABELS, settings?.get('strengthLevel'))} />
          <DetailFieldComponent label="Training focus" source="Settings.trainingFocus"
            value={describeEnum(TRAINING_FOCUS_LABELS, settings?.get('trainingFocus'))} />
          <DetailFieldComponent label="Water confidence" source="Settings.waterConfidence"
            value={describeSettingsFlag(settings, 'waterConfidence')} />
          <DetailFieldComponent label="Swim days" source="latest ScheduleDays row for the member (what setScheduleDays updates)"
            value={scheduleDays ? joinList(activeSwimDays(scheduleDays)) || 'none active' : null} />
          <DetailFieldComponent label="Strokes" source="Settings.swimStrokes" wide>
            {this.renderStrokes()}
          </DetailFieldComponent>
          <DetailFieldComponent label="Swim goals" source="Settings.goalsSwim"
            value={goalList(settings?.get('goalsSwim'))} />
          <DetailFieldComponent label="Dryland goals" source="Settings.goalsDryland"
            value={goalList(settings?.get('goalsDryland'))} />
          <DetailFieldComponent label="Injuries" source="Settings.injuries"
            value={joinList(settings?.get('injuries'))} />
          <DetailFieldComponent label="Has dryland equipment" source="Settings.hasEquipment"
            value={describeSettingsFlag(settings, 'hasEquipment')} />
          <DetailFieldComponent label="Equipment" source="Settings.equipment"
            value={joinList(settings?.get('equipment'))} />
          <DetailFieldComponent label="Heart rate zones" source="Settings.heartRateZones" wide
            value={this.renderHeartRateZones(settings?.get('heartRateZones'))} />
          <DetailFieldComponent label="Apple Watch screens" source="Settings.appleWatchConfiguration" wide>
            {settings?.get('appleWatchConfiguration')
              ? this.renderAppleWatchConfiguration(settings.get('appleWatchConfiguration'))
              : null}
          </DetailFieldComponent>
        </DetailGrid>
        <div className="detail-subheading">App settings</div>
        <DetailGrid>
          <DetailFieldComponent label="Notifications" source="Settings.isNotificationOn"
            value={describeSettingsFlag(settings, 'isNotificationOn')} />
          <DetailFieldComponent label="Weekly plan email" source="Settings.trainingPlanWeeklyEmailEnabled"
            value={describeSettingsFlag(settings, 'trainingPlanWeeklyEmailEnabled')} />
          <DetailFieldComponent label="Skipped first-workout setup" source="Settings.isFirstWorkoutConfigurationSkipped"
            value={describeSettingsFlag(settings, 'isFirstWorkoutConfigurationSkipped')} />
        </DetailGrid>
        {this.renderLegacyFields(member, USER_POOL_LEGACY_FIELDS)}
        {this.renderLegacyFields(settings, SETTINGS_LEGACY_FIELDS)}
      </DetailSectionComponent>
    )
  }

  renderAppleWatchConfiguration = (config) => {
    if (!config) return <i>No Configuration</i>

    const slots = (screen) => ['top', 'bottomLeft', 'bottomRight']
      .map((slot) => screen?.[slot] || '-')
      .join(' / ')
    const renderConfig = (type, mode) => (
      <div className="col-sm-4" key={type}>
        <b>{type}</b>
        <div className="small">Primary: {slots(mode.primary)}</div>
        <div className="small">Secondary: {slots(mode.secondary)}</div>
      </div>
    )

    return (
      <div className="row">
        {config.poolSwim && renderConfig('Pool swim', config.poolSwim)}
        {config.openWater && renderConfig('Open water', config.openWater)}
        {config.guidedWorkout && renderConfig('Guided workout', config.guidedWorkout)}
      </div>
    )
  }

  getReferralStats = () => {
    const id = this.props.id || this.props.match.params.id
    Parse.Cloud.run('getUserReferralCode', { userId: id }).then(
      (results) => {
        this.setState({
          referralCode: results.code
        })
      },
      (error) => {
        console.error('Error getting referral code: ' + error.message)
      }
    )
    Parse.Cloud.run('getCouponCodeRedemptionCount', { userId: id }).then(
      (results) => {
        this.setState({
          referralCount: results.count || 0
        })
      },
      (error) => {
        console.error('Error getting referral code: ' + error.message)
      }
    )
  }

  readFitFile = (event) => {
    const file = event.target.files[0]

    if (file) {
      const activityId = file.name.slice(0, file.name.indexOf('.'))
      this.setState({
        isLoggingFitFile: true
      })
      const reader = new FileReader()
      reader.onload = (e) => {
        console.log(
          '[readFitFile] Logging activity ID ' +
            activityId +
            ' for ' +
            this.state.member.id +
            '...'
        )
        const base64 = e.target.result.split(',')[1]
        const params = {
          data: base64,
          userId: this.state.member.id,
          activityId: activityId
        }
        Parse.Cloud.run('fitFileUpload', params).then(
          (results) => {
            window.location.reload(false)
          },
          (error) => {
            window.alert('Error logging fit file: ' + error.message)
          }
        )
      }
      reader.onerror = (e) => {
        // error occurred
        console.log('[readFitFile] Error : ' + e.type)
        window.alert('Error getting file: ' + e.message)
      }
      reader.readAsDataURL(file)
    }
  }

  renderAchievements = () => {
    if (!this.state.achievements) return <i>Loading...</i>
    if (this.state.achievements.length === 0) return <i>None</i>
    return (
      <BoundedListComponent items={this.state.achievements} noun="achievements">
        {(visible) => (
          <ul className="mb-0">
            {visible.map((obj) => (
              <li key={obj.id}>
                {obj.get('achievement').get('name')}{' '}
                <small className="text-muted">
                  {obj.get('dateCompleted') ? formatDate(obj.get('dateCompleted')) : ''}
                </small>
              </li>
            ))}
          </ul>
        )}
      </BoundedListComponent>
    )
  }

  renderDynamicIntervals = () => {
    if (!this.state.dynamicIntervals) return <i>Loading...</i>
    if (this.state.dynamicIntervals.length === 0) return <i>None</i>
    return (
      <BoundedListComponent items={this.state.dynamicIntervals} limit={10} noun="intervals">
        {(visible) => (
          <table>
            <tbody>
              {visible.map((obj) => (
                <tr key={obj.id}>
                  <td>
                    {obj.distance} {obj.poolCourse === 0 ? 'yd' : 'm'} {getStrokeString(obj.stroke)}{' '}
                  </td>
                  <td>
                    &nbsp;db:
                    <code>{' '}{formatSeconds(obj.time)}</code>
                  </td>
                  <td>
                    &nbsp;app:
                    <code>{' '}{this.getPTITimeString(obj)}</code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </BoundedListComponent>
    )
  }

  getPTITimeString = (obj) => {
    const isTimeYards = obj.poolCourse === 0
    const isDisplayYards = this.getDefaultPool()?.isDisplayInYards === true
    const time = convertPTI(obj.time, !isTimeYards, !isDisplayYards)
    return formatSeconds(time)
  }

  getDefaultPool = () => {
    const pool = this.state.member.get('pools')?.find((element) => { return element.isDefault })
    return pool
  }

  getHTMLSafeAdminNotes = () => {
    const notes = this.state.userInfo.get('adminNotes')
    if (!notes) return '-'
    return notes.split('\n').map((value, index) => {
      return (
        <span key={index}>
          {value}
          <br />
        </span>
      )
    })
  }

  renderStatsSection = () => {
    const { userInfo } = this.state
    if (!userInfo) {
      return (
        <DetailSectionComponent id="member-stats" title="Stats & streaks" collapsible defaultOpen>
          <p className="text-danger mb-0">No UserInfo_v3 linked</p>
        </DetailSectionComponent>
      )
    }
    const swolfCount = userInfo.get('swolfFreestyleCount_v5')
    const swolfAverage = swolfCount > 0
      ? userInfo.get('swolfFreestyleSum_v5') / swolfCount
      : undefined
    const totalTime = userInfo.get('totalTime')
    const streakBusy = this.state.isResettingStreak || this.state.isRecalculatingStreak || this.state.isLoadingStreaks
    return (
      <DetailSectionComponent id="member-stats" title="Stats & streaks" subtitle={userInfo.id}
        collapsible defaultOpen>
        <DetailGrid>
          <DetailStatComponent label="Workouts" source="UserInfo_v3.totalWorkoutsCompleted"
            value={formatNumber(userInfo.get('totalWorkoutsCompleted'), 0)}
            action={<a href="" onClick={this.handleEditTotalWorkouts}>Edit</a>} />
          <DetailStatComponent label="Distance" unit="m" source="UserInfo_v3.totalDistance (meters)"
            value={formatNumber(userInfo.get('totalDistance'), 0)}
            action={<a href="" onClick={this.handleEditTotalDistance}>Edit</a>} />
          <DetailStatComponent label="Time" unit="h" source="UserInfo_v3.totalTime (seconds)"
            value={typeof totalTime === 'number' ? formatNumber(totalTime / 3600, 1) : EMPTY}
            action={<a href="" onClick={this.handleEditTotalTime}>Edit</a>} />
          <DetailStatComponent label="Free SWOLF avg" source="UserInfo_v3.swolfFreestyleSum_v5 / swolfFreestyleCount_v5"
            value={formatNumber(swolfAverage, 1)} />
          <DetailStatComponent label="Longest streak" unit="days" source="UserInfo_v3.longestStreak"
            value={formatNumber(userInfo.get('longestStreak'), 0)} />
          <DetailStatComponent label="Cached streak" unit="weeks" source="UserInfo_v3.workoutStreakCachedWeeks_v4"
            value={formatNumber(userInfo.get('workoutStreakCachedWeeks_v4'), 0)} />
        </DetailGrid>
        <div className="detail-subheading">Streaks (live)</div>
        <DetailGrid>
          <DetailFieldComponent label="Workout streak" source="getGuidedWorkoutStreakCount"
            value={this.workoutStreakLabel()} />
          <DetailFieldComponent label="Tip of the Day streak" source="getTipOfTheDayStreakCount"
            value={this.totdStreakLabel()} />
          <DetailFieldComponent label="Cached through workout on" source="UserInfo_v3.workoutStreakCachedLastWorkoutDate_v4"
            value={userInfo.get('workoutStreakCachedLastWorkoutDate_v4')
              ? formatDate(userInfo.get('workoutStreakCachedLastWorkoutDate_v4'))
              : null} />
          <DetailFieldComponent label="Test sets dismissed" source="UserInfo_v3.testSetDismissedCount"
            value={formatNumber(userInfo.get('testSetDismissedCount'), 0)} />
        </DetailGrid>
        <div className="mt-2">
          <button
            className="btn btn-sm btn-outline-danger me-2"
            onClick={this.handleResetStreakCache}
            disabled={streakBusy}
          >
            {this.state.isResettingStreak ? 'Resetting...' : 'Reset Streak Cache'}
          </button>
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={this.handleRecalculateStreak}
            disabled={streakBusy}
          >
            {this.state.isRecalculatingStreak ? 'Recalculating...' : 'Recalculate Now'}
          </button>
        </div>
        <DetailMore
          label={<span title="UserInfo_v3.yearlyTotals_v9. Despite its name, distanceMeters is the raw display-unit total.">Yearly totals</span>}
          count={yearlyTotalsRows(userInfo.get('yearlyTotals_v9')).length}>
          <YearlyTotalsTableComponent yearlyTotals={userInfo.get('yearlyTotals_v9')} />
        </DetailMore>
        {this.renderLegacyFields(userInfo, USER_INFO_STATS_LEGACY_FIELDS)}
      </DetailSectionComponent>
    )
  }

  renderPlanLink = (planId) => planId
    ? <a href={'/#/trainingplans/' + planId}>{planId}</a>
    : null

  getActivePlanRows = () => {
    const { training } = this.state
    return activePlanRows(training?.get('activePlans'), [
      training?.get('activeSwimPlanId'),
      training?.get('activeStrengthPlanId')
    ])
  }

  // Appended as plans finish, so newest last; shown newest first.
  getPlanHistory = () =>
    (this.state.userInfo?.get('trainingPlanHistory') || []).filter(Boolean).reverse()

  renderActivePlans = () => {
    const rows = this.getActivePlanRows()
    if (rows.length === 0) {
      return <p className="text-muted mb-0">None</p>
    }
    return (
      <BoundedListComponent items={rows} noun="plans">
        {(visible) => (
          <table className="table table-sm mb-0">
            <thead>
              <tr><th>Template</th><th>Member&apos;s plan</th></tr>
            </thead>
            <tbody>
              {visible.map((row) => (
                <tr key={row.key}>
                  <td>
                    {describePlanKey(row.key)}
                    {row.isCurrent && <span className="badge text-bg-success ms-1">current</span>}
                  </td>
                  <td>{this.renderPlanLink(row.planId)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </BoundedListComponent>
    )
  }

  renderPlanHistory = () => {
    const history = this.getPlanHistory()
    if (history.length === 0) {
      return <p className="text-muted mb-0">None</p>
    }
    return (
      <BoundedListComponent items={history} noun="plans">
        {(visible) => (
          <ul className="mb-0">
            {visible.map((plan, index) => (
              <li key={plan.id + '-' + index}>
                <a href={'/#/trainingplans/' + plan.id}>{plan.get('title') || plan.id}</a>
              </li>
            ))}
          </ul>
        )}
      </BoundedListComponent>
    )
  }

  renderCallDate = (key) => {
    const value = this.state.member.get(key)
    return value ? format(parseISO(value), 'dd/MM/yyyy') : null
  }

  renderTrainingSection = () => {
    const { member, training, userInfo } = this.state
    const tempWorkout = member.get('tempWorkout')
    return (
      <DetailSectionComponent id="member-training" title="Training plans" collapsible defaultOpen
        subtitle={joinSummary([
          this.getActivePlanRows().length + ' active',
          this.getPlanHistory().length + ' completed'
        ])}>
        <DetailGrid>
          <DetailFieldComponent label="Active swim plan" source="UserTraining.activeSwimPlanId">
            {this.renderPlanLink(training?.get('activeSwimPlanId'))}
          </DetailFieldComponent>
          <DetailFieldComponent label="Active dryland plan" source="UserTraining.activeStrengthPlanId">
            {this.renderPlanLink(training?.get('activeStrengthPlanId'))}
          </DetailFieldComponent>
          <DetailFieldComponent label="WOD trial start" source="UserInfo_v3.wodTrialStart"
            value={userInfo?.get('wodTrialStart') ? formatDate(userInfo.get('wodTrialStart')) : null} />
          <DetailFieldComponent label="Quick Log draft" source="_User.tempWorkout"
            value={tempWorkout
              ? `${tempWorkout.title || 'Untitled'} · ${formatNumber(tempWorkout.totalDistance, 0)}`
              : null} />
        </DetailGrid>
        <div className="detail-subheading">All active plans</div>
        {this.renderActivePlans()}
        <div className="detail-subheading">Completed plans</div>
        {this.renderPlanHistory()}
        <DetailMore label="Overrides & coaching calls">
          <DetailGrid>
            <DetailFieldComponent label="Garmin sync token" source="_User.garminAccessToken (first 4 characters)"
              value={member.get('garminAccessToken') ? member.get('garminAccessToken').substring(0, 4) : null} />
            <DetailFieldComponent label="Onboarding call" source="_User.onboardingCallDate"
              value={this.renderCallDate('onboardingCallDate')} />
            <DetailFieldComponent label="Coach call" source="_User.coachCallDate"
              value={this.renderCallDate('coachCallDate')} />
          </DetailGrid>
          <a href="" className="d-inline-block mt-2 small" onClick={this.handleOpenMemberInfoModal}>Edit</a>
        </DetailMore>
        {this.renderLegacyFields(training, TRAINING_LEGACY_FIELDS)}
      </DetailSectionComponent>
    )
  }

  handleOpenMemberInfoModal = (event) => {
    event.preventDefault()
    this.setState({
      form: {
        skillLevel: this.state.member.get('skillLevel'),
        garminAccessToken:
          this.state.member.get('garminAccessToken') || null,
        coachCallDate:
          (this.state.member.get('coachCallDate') &&
            new Date(this.state.member.get('coachCallDate'))) ||
          new Date(),
        onboardingCallDate:
          (this.state.member.get('onboardingCallDate') &&
            new Date(
              this.state.member.get('onboardingCallDate')
            )) ||
          new Date()
      },
      modalIsOpen: true
    })
  }

  // Notes support leaves for support, so they lead the page when there are any.
  renderAdminNotesSection = () => {
    const { userInfo } = this.state
    const hasNotes = !!userInfo?.get('adminNotes')
    return (
      <DetailSectionComponent id="member-notes" title="Admin notes" collapsible
        defaultOpen={hasNotes}
        subtitle={hasNotes ? null : 'none'}
        actions={userInfo && (
          <a href="" onClick={this.handleAdminNoteEdit}>
            {this.state.isEditingAdminNotes ? 'Save' : 'Edit'}
          </a>
        )}>
        {!userInfo
          ? <i>No Info</i>
          : this.state.isEditingAdminNotes
            ? (
              <textarea
                className="form-control"
                rows={6}
                value={this.state.tempAdminNotes}
                onChange={this.handleAdminNoteChange}
              />
              )
            : this.getHTMLSafeAdminNotes()}
      </DetailSectionComponent>
    )
  }

  // Coach-related sections, conversations first since support opens those most.
  renderCoachSections = () => {
    const { member, memberMemories, coachConversations } = this.state
    const latestConversation = coachConversations?.[0]
    return (
      <>
        <DetailSectionComponent id="member-coach" title="Coach Agent conversations" collapsible
          subtitle={latestConversation
            ? `${coachConversations.length} · latest ${formatDate(latestConversation.get('lastMessageAt') || latestConversation.createdAt)}`
            : countText(coachConversations)}>
          <div className="detail-table-scroll">{this.renderCoachConversations()}</div>
        </DetailSectionComponent>
        <DetailSectionComponent title="Coach memories" collapsible
          subtitle={countText(memberMemories)}>
          <div className="detail-table-scroll">{this.renderMemories()}</div>
        </DetailSectionComponent>
        <DetailSectionComponent title="Academy coaching" collapsible
          subtitle={member.get('sendbirdChannelId') ? 'chat linked' : 'no chat'}>
          {member.get('sendbirdChannelId')
            ? (
              <DetailFieldComponent label="Sendbird channel" source="_User.sendbirdChannelId">
                <a
                  href={`https://dashboard.sendbird.com/12EAF638-5874-4B15-BD6F-5FD2A30976C4/group-channels/${member.get('sendbirdChannelId')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {member.get('sendbirdChannelId')}
                </a>
              </DetailFieldComponent>
              )
            : (
              <div>
                <button
                  className="btn btn-primary me-2"
                  onClick={this.handleCreateAcademyChat}
                  disabled={this.state.isCreatingWelcomeChat || this.state.isCreatingEmptyChat}
                >
                  {this.state.isCreatingWelcomeChat ? 'Creating...' : 'Create Welcome Chat'}
                </button>
                <button
                  className="btn btn-outline-primary"
                  onClick={this.handleCreateEmptyAcademyChat}
                  disabled={this.state.isCreatingWelcomeChat || this.state.isCreatingEmptyChat}
                >
                  {this.state.isCreatingEmptyChat ? 'Creating...' : 'Create Empty Chat'}
                </button>
              </div>
              )}
        </DetailSectionComponent>
      </>
    )
  }

  renderSubscriptionSection = () => {
    const { member } = this.state
    const extensions = [...this.state.subscriptionExtensions].sort((a, b) =>
      (toDate(b.createdAt)?.getTime() || 0) - (toDate(a.createdAt)?.getTime() || 0))
    return (
      <DetailSectionComponent id="member-subscription" title="Subscription" collapsible defaultOpen
        subtitle={extensions.length > 0 ? `${extensions.length} extension${extensions.length === 1 ? '' : 's'}` : null}
        actions={
          <a href={'https://app.revenuecat.com/projects/' + Config.revenueCatProjectId + '/customers/' + member.id}
            target='_blank' rel='noreferrer' className="btn btn-sm btn-primary">
            RevenueCat history
          </a>
        }>
        <p className="small text-muted">
          RevenueCat is the source of truth for subscription state.
        </p>
        <button className="btn btn-sm btn-outline-primary me-2" onClick={this.extendSubscriptionProd}>
          Extend Subscription
        </button>
        <button className="btn btn-sm btn-outline-secondary" onClick={this.linkStripeSubscription}>
          Link Stripe Subscription
        </button>
        <div className="detail-subheading">Extension history</div>
        {extensions.length === 0
          ? <p className="text-muted mb-0">None</p>
          : (
            <BoundedListComponent items={extensions} noun="extensions">
              {(visible) => (
                <ul className="mb-0">
                  {visible.map((extension) => (
                    <li key={extension.objectId}>
                      <b>+{extension.days} days ({extension.platform})</b>: {formatDateTime(extension.createdAt)}
                    </li>
                  ))}
                </ul>
              )}
            </BoundedListComponent>
            )}
        {this.renderLegacyFields(member, USER_SUBSCRIPTION_LEGACY_FIELDS)}
      </DetailSectionComponent>
    )
  }

  renderActivitiesSection = () => (
    <DetailSectionComponent id="member-activities" title="Completed activities"
      subtitle={this.numberOfCompletedWorkoutsText()} collapsible defaultOpen
      actions={
        <label className="small mb-0">
          <b>{this.state.isLoggingFitFile ? 'Logging...' : 'Log from fit file:'}</b>{' '}
          <input
            disabled={this.state.isLoggingFitFile}
            type="file"
            onChange={(event) => {
              this.readFitFile(event)
            }}
            id="file_upload"
          />
        </label>
      }>
      <CompletedActivitiesTableComponent workouts={this.state.workouts} />
    </DetailSectionComponent>
  )

  renderRawSection = () => {
    const { member, userInfo, settings, training, scheduleDays } = this.state
    const objects = [
      [member, USER_SHOWN_FIELDS],
      [userInfo, USER_INFO_SHOWN_FIELDS],
      [settings, SETTINGS_SHOWN_FIELDS],
      [training, TRAINING_SHOWN_FIELDS],
      [scheduleDays, SCHEDULE_DAYS_SHOWN_FIELDS]
    ].filter(([object]) => object)
    return (
      <DetailSectionComponent id="member-raw" title="All fields" collapsible
        subtitle={rawFieldsSummary(objects)}>
        <p className="small text-muted">Every stored field. Credentials are masked.</p>
        {objects.map(([object, shownKeys]) => (
          <RawFieldsComponent key={object.className} object={object} shownKeys={shownKeys} />
        ))}
      </DetailSectionComponent>
    )
  }

  handleChange = (otp) => {
    const updates = this.state.form
    updates.garminAccessToken = otp
    this.setState({
      form: updates
    })
  }

  closeModal = () => {
    this.setState({
      modalIsOpen: false,
      form: {
        skillLevel: null,
        garminAccessToken: null,
        coach_call: '',
        onboarding_call: ''
      }
    })
  }

  loadLibrary = async () => {
    const id = this.props.id || this.props.match.params.id
    if (!id) {
      return
    }
    const user = new Parse.User()
    user.id = id
    const query = new Parse.Query('UserSavedWorkout')
    query.equalTo('user', user)
    query.include('workout')
    // Newest first, so the short list shows the latest saves.
    query.descending('createdAt')
    query.limit(1000)
    const lib = await query.find()
    this.setState({
      memberLibrary: lib
    })
  }

  loadMemories = async () => {
    const id = this.props.id || this.props.match.params.id
    if (!id) {
      return
    }
    try {
      const user = new Parse.User()
      user.id = id
      const query = new Parse.Query('SwimCoachMemory')
      query.equalTo('user', user)
      query.descending('createdAt')
      query.limit(1000)
      const memories = await query.find()
      this.setState({
        memberMemories: memories
      })
    } catch (e) {
      console.error('Failed to load coach memories', e)
      this.setState({
        memberMemories: []
      })
    }
  }

  renderMemories = () => {
    if (!this.state.memberMemories) return <i>Loading...</i>
    if (this.state.memberMemories.length === 0) return <i>No coach memories</i>

    return (
      <BoundedListComponent items={this.state.memberMemories} noun="memories">
        {(visible) => (
          <ul className="mb-0">
            {visible.map((memory) => (
              <li key={memory.id}>
                {memory.get('content')}{' '}
                <small className="text-muted">
                  ({memory.get('category')})
                </small>
              </li>
            ))}
          </ul>
        )}
      </BoundedListComponent>
    )
  }

  loadCoachConversations = async () => {
    const id = this.props.id || this.props.match.params.id
    if (!id) {
      return
    }
    try {
      const user = new Parse.User()
      user.id = id
      const query = new Parse.Query('SwimCoachConversation')
      query.equalTo('user', user)
      query.descending('lastMessageAt')
      query.limit(1000)
      const conversations = await query.find()
      this.setState({
        coachConversations: conversations
      })
    } catch (e) {
      console.error('Failed to load coach conversations', e)
      this.setState({
        coachConversations: []
      })
    }
  }

  renderCoachConversations = () => {
    if (!this.state.coachConversations) return <i>Loading...</i>
    if (this.state.coachConversations.length === 0) return <i>No coach conversations</i>

    return (
      <BoundedListComponent items={this.state.coachConversations} noun="conversations">
        {(visible) => (
          <ul className="mb-0">
            {visible.map((conversation) => {
              const lastMessageAt = conversation.get('lastMessageAt') || conversation.get('createdAt')
              const sourceMeta = getSourceMeta(conversation.get('entityType'))
              const entityId = conversation.get('entityId')
              return (
                <li key={conversation.id}>
                  <a href={'/#/swimCoachConversations/' + conversation.id}>
                    {lastMessageAt ? lastMessageAt.toLocaleString() : '(no messages)'}
                  </a>
                  {sourceMeta && entityId
                    ? (
                      <small className="text-muted">
                        {' · '}
                        <a href={'/#' + sourceMeta.path + entityId}>{sourceMeta.label}</a>
                      </small>
                      )
                    : ''}
                  {conversation.get('lockedAt')
                    ? (<span className="badge text-bg-danger ms-1">Locked</span>)
                    : ''}
                </li>
              )
            })}
          </ul>
        )}
      </BoundedListComponent>
    )
  }

  renderLibrary = () => {
    if (!this.state.memberLibrary) return <div>Loading...</div>
    if (this.state.memberLibrary.length === 0) return <i>None</i>

    return (
      <BoundedListComponent items={this.state.memberLibrary} noun="workouts">
        {(visible) => (
          <ul className="mb-0">
            {visible.map((obj) => {
              if (!obj.get('workout')) return <li key={obj.id}>⚠️ Error { obj.id }</li>
              return (
                <li key={obj.id}>
                  <a href={'/#/workouts/' + obj.get('workout').id}>
                    {obj.get('workout').get('title') || obj.id}
                  </a>
                </li>
              )
            })}
          </ul>
        )}
      </BoundedListComponent>
    )
  }

  setMemberInfo = async (e) => {
    e.preventDefault()

    const form = this.state.form

    const user = {
      objectId: this.state.member.id,
      garminAccessToken: form.garminAccessToken,
      coachCallDate: formatISO(form.coachCallDate),
      onboardingCallDate: formatISO(form.onboardingCallDate),
      skillLevel: parseInt(form.skillLevel)
    }

    Parse.Cloud.run('updateUser', { user: user }).then(
      (results) => {
        this.reloadAllMemberData()
      },
      (error) => {
        window.alert('Error setting member info: ' + error.message)
      }
    )

    this.setState({
      modalIsOpen: false,
      form: {
        skillLevel: null,
        garminAccessToken: null,
        coach_call: '',
        onboarding_call: ''
      }
    })
  }

  linkStripeSubscription = async (e) => {
    e?.preventDefault()

    const subscriptionId = prompt('Enter Stripe Subscription Id (sub_XXXXX)')
    if (!subscriptionId) return

    const body = {
      userId: this.state.member.id,
      stripeSubscriptionId: subscriptionId
    }

    Parse.Cloud.run('linkStripeSubscriptionIdToUser', body).then(
      (results) => {
        this.reloadAllMemberData()
        window.alert('Subscription linked successfully')
      },
      (error) => {
        window.alert('Error linking subscription: ' + error.message)
      }
    )
  }

  extendSubscriptionProd = async (e) => {
    e?.preventDefault()
    await this.extendSubscription(false)
  }

  extendSubscriptionSandbox = async (e) => {
    e?.preventDefault()
    await this.extendSubscription(true)
  }

  extendSubscription = async (isSandbox) => {
    const numDaysText = prompt('How many days (max 90)?')
    if (!numDaysText) return
    const numDays = parseInt(numDaysText)
    if (isNaN(numDays)) {
      alert('Invalid number of days')
      return
    }

    const body = {
      userId: this.state.member.id,
      extensionDays: numDays,
      isSandbox
    }

    Parse.Cloud.run('extendSubscription', body).then(
      (results) => {
        this.reloadAllMemberData()
      },
      (error) => {
        window.alert('Error extending subscription: ' + error.message)
      }
    )
  }

  render = () => {
    if (!this.state.member) return <div className="container py-4">Loading...</div>
    const { member, userInfo, settings } = this.state
    const historySources = [
      { className: '_User', object: member },
      { className: 'UserInfo_v3', object: userInfo },
      { className: 'Settings', object: settings }
    ]
    const historyCount = mergeFieldHistory(historySources).length
    return (
      <div className="container py-4">
        {this.renderHeader()}
        <DetailJumpNavComponent sections={MEMBER_SECTIONS} />
        {/* What support needs on most visits, open. */}
        <div className="row">
          <div className="col-lg-6">
            {this.renderAdminNotesSection()}
            {this.renderAccountSection()}
          </div>
          <div className="col-lg-6">
            {this.renderSubscriptionSection()}
            {this.renderTrainingSection()}
          </div>
        </div>
        {this.renderStatsSection()}
        {this.renderActivitiesSection()}
        {/* Everything else starts collapsed, with a summary in its header.
            Six a side, so the collapsed headers read as two even lists. */}
        <div className="row">
          <div className="col-lg-6">
            {this.renderProfileSection()}
            {this.renderPreferencesSection()}
            {this.renderIntegrationsSection()}
            {this.renderCoachSections()}
          </div>
          <div className="col-lg-6">
            <DetailSectionComponent id="member-library" title="Library workouts" collapsible
              subtitle={countText(this.state.memberLibrary)}>
              <div className="detail-table-scroll">{this.renderLibrary()}</div>
            </DetailSectionComponent>
            <DetailSectionComponent id="member-achievements" title="Achievements" collapsible
              subtitle={countText(this.state.achievements)}
              actions={<button className="btn btn-link btn-sm p-0" onClick={this.handleNewAward}>Award new</button>}>
              <div className="detail-table-scroll">{this.renderAchievements()}</div>
            </DetailSectionComponent>
            <DetailSectionComponent title="Personalized training intervals" collapsible
              subtitle={countText(this.state.dynamicIntervals)}>
              <p className="small text-muted">loadTimes_v4</p>
              <div className="detail-table-scroll">{this.renderDynamicIntervals()}</div>
            </DetailSectionComponent>
            <MemberActivityDataComponent memberId={member.id} />
          </div>
        </div>
        <DetailSectionComponent id="member-history" title="Change history" collapsible
          subtitle={`${historyCount} change${historyCount === 1 ? '' : 's'}`}>
          <p className="small text-muted">
            fieldHistory on _User, UserInfo_v3 and Settings (last 10 per class)
          </p>
          <FieldHistoryComponent sources={historySources} />
        </DetailSectionComponent>
        {this.renderRawSection()}

        <div className="d-flex justify-content-end mb-4">
          <button
            className="btn btn-danger btn-sm"
            onClick={this.handleDeleteAccount}
          >
            Delete Account
          </button>
        </div>

        <Modal
          isOpen={this.state.modalIsOpen}
          onRequestClose={this.closeModal}
          style={customStyles}
          contentLabel="Example Modal"
        >
          <form onSubmit={this.setMemberInfo}>
            <div className="mb-3">
              <label htmlFor="exampleInputEmail1">Skill Level </label>

              <select
                className="form-select"
                value={this.state.form.skillLevel}
                name="skillLevel"
                onChange={(event) => {
                  const updates = this.state.form
                  updates.skillLevel = event.target.value
                  this.setState({
                    form: updates
                  })
                }}
              >
                <options value="">Select SkillLevel</options>
                {options.map((item) => {
                  return <option key={item.value} value={item.value}>{item.label}</option>
                })}
              </select>
            </div>

            <div className="mb-3">
              <label htmlFor="exampleInputEmail1">Garmin Sync Token </label>
              <OtpInput
                value={this.state.form.garminAccessToken}
                onChange={this.handleChange}
                numInputs={4}
                separator={<span className="ms-1 me-1">-</span>}
                inputStyle={{ width: 60 }}
              />
            </div>

            <div className=" row mt-2">
              <label htmlFor=" col-sm-12 ml-2" style={{ paddingLeft: 16 }}>
                Onboarding Call{' '}
              </label>
              <div className="col-sm-12">
                <DatePicker
                  showMonthDropdown
                  showYearDropdown
                  id="onboarding_call"
                  name="onboarding_call"
                  dateFormat="dd/MM/yyyy"
                  title="Click to select a Coach Call"
                  placeholderText="Click to select a Coach Call "
                  className="form-control"
                  onChange={(date) => {
                    const updates = this.state.form
                    updates.onboardingCallDate = date
                    this.setState({
                      form: updates
                    })
                  }}
                  selected={this.state.form.onboardingCallDate}
                />
              </div>
            </div>

            <div className=" row mt-2">
              <label htmlFor=" col-sm-12 ml-2" style={{ paddingLeft: 16 }}>
                Coach Call{' '}
              </label>
              <div className="col-sm-12">
                <DatePicker
                  showMonthDropdown
                  showYearDropdown
                  id="coach_call"
                  name="coach_call"
                  dateFormat="dd/MM/yyyy"
                  title="Click to select a Coach Call"
                  placeholderText="Click to select a Coach Call "
                  className="form-control"
                  onChange={(date) => {
                    const updates = this.state.form
                    updates.coachCallDate = date
                    this.setState({
                      form: updates
                    })
                  }}
                  selected={this.state.form.coachCallDate}
                />
              </div>
            </div>

            <button type="submit " className="btn btn-primary mt-4">
              Save & Update
            </button>
          </form>
        </Modal>
      </div>
    )
  }
}

MemberDetail.propTypes = {
  id: PropTypes.string,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string
    })
  })
}
